/**
 * Privacy Policy and Terms of Service text, shared by Web and Desktop.
 *
 * Single source of truth: there is no CMS in this app, and legal text
 * must not be editable without a deploy and a review. Every factual
 * claim here was checked against the code - see
 * docs/production-readiness.md §2 for the data-flow audit behind the
 * "Who we share data with" table. If the system changes, this file
 * changes with it, and TERMS_VERSION goes up.
 *
 * TERMS_VERSION is duplicated in backend/core/legal.py, which is the
 * authority - the backend decides whether a user still needs to accept.
 * The copy here exists only to render "version X, effective Y" on the
 * page; a drift between the two is cosmetic, not functional.
 *
 * Two documents: Privacy Policy and Terms of Service. Payments, cancellation
 * and the no-refund rule are a section of the Terms (id
 * 'payments-cancellation'); PAYMENT_NOTICE is the short form of it shown on
 * plan-selection screens before a customer pays.
 * Public text states only what the implementation supports; where a fact is
 * not decided or not verified it is left out or worded generally. Nothing
 * internal (no "[CONFIRM]" notes) belongs on the public pages. The open
 * business items, and whether the documents are ready to publish, are
 * tracked in docs/legal-publication-checklist.md.
 *
 * Written for the Indian Digital Personal Data Protection Act, 2023,
 * because KrishvaQ is priced in INR and sold in India. It deliberately
 * does NOT cover the GDPR: that Regulation reaches a business outside
 * the EU only when it TARGETS people there, and pricing in one currency
 * for one market is the clearest evidence of not doing so. If EUR/GBP
 * pricing or EU-language marketing is ever added, that changes and this
 * file needs the GDPR material back - see git history for the version
 * that had it.
 *
 * NOT LEGAL ADVICE. This describes what the software actually does; it
 * is not a substitute for independent legal advice about your own
 * obligations.
 */

/** Bump on any MATERIAL change - it forces every user to re-accept. */
export const TERMS_VERSION = '2026-10-30.2';

export const TERMS_EFFECTIVE_DATE = '30 October 2026';

/** Public website, used in the documents and for the public page paths. */
export const WEBSITE_URL = 'https://krishvaq.krishvatech.com';

/**
 * Placeholders that must be replaced before publishing. Rendered
 * visibly rather than silently, so an unfilled one cannot ship
 * unnoticed - see the LegalPage renderers in Web and Desktop.
 */
export const COMPANY = {
  legalName: 'KrishvaTech Pvt. Ltd.',
  tradingName: 'KrishvaQ',
  address: 'Surat, Gujarat, India',
  privacyEmail: 'info@krishvatech.com',
  supportEmail: 'info@krishvatech.com',
  grievanceOfficer: 'Avinash',
  jurisdiction: 'Surat, Gujarat, India',
};

/** Public page path for each document. The same paths are used by the
 *  website routes, the footer links and the links between the documents. */
const PRIVACY_PATH = '/privacy-policy';
const TERMS_PATH = '/terms-of-service';

export const PRIVACY_POLICY = {
  id: 'privacy',
  path: PRIVACY_PATH,
  title: 'Privacy Policy',
  intro:
    'This policy explains what personal data KrishvaQ collects, why, who ' +
    'it is shared with, how long we keep it, and the control you have over ' +
    'it. It is written to meet the requirements of the Digital Personal ' +
    'Data Protection Act, 2023 and the rules made under it, as they come ' +
    'into force. It covers the KrishvaQ website, web application and ' +
    'desktop application.',
  sections: [
    {
      id: 'who-we-are',
      title: 'Who we are',
      body: [
        `KrishvaQ (${WEBSITE_URL}) is operated by ${COMPANY.legalName}, of ${COMPANY.address}.`,
        'Under the DPDP Act we are the Data Fiduciary for the personal data described below — we decide why and how it is processed, and we are answerable for it.',
        `Our Privacy & Grievance Contact is ${COMPANY.grievanceOfficer}, reachable at ${COMPANY.privacyEmail}.`,
      ],
    },
    {
      id: 'features',
      title: 'What KrishvaQ does with your data',
      body: [
        'KrishvaQ is an AI meeting and interview assistant. To provide each feature, the data listed in the next section is processed as follows:',
      ],
      list: {
        items: [
          'Real-time AI answers — questions detected in the conversation, with relevant context from the meeting and your documents, are sent to an AI provider to draft an answer.',
          'Live transcription and translation — meeting audio is sent to a speech-recognition provider to produce text, and text is sent to a translation provider when you ask for translation.',
          'Private AI overlay (desktop) — shows answers in a window that is hidden from screen sharing on supported setups.',
          'Screenshot and screen analysis — when you ask a question about your screen, the screenshots you choose to attach are sent to an AI provider so it can read them and answer.',
          'CV and document-based answers — documents you upload are read, split into passages and searched so answers can draw on your own material.',
          'Meeting summaries, action items, Meeting Memory and history — generated from the transcript after a meeting and stored so you can search and reopen past meetings.',
          'Technical-interview assistance and personalised responses — answers are shaped by the meeting type, languages and style preferences you set.',
          'Auto Attend meeting agent — a bot that joins a Google Meet or Microsoft Teams meeting on your behalf. See “Auto Attend”, below.',
        ],
      },
    },
    {
      id: 'what-we-collect',
      title: 'What we collect, and what we do with it',
      body: [
        'This is the itemised notice the DPDP Act requires. Each row is personal data we hold and the purpose we hold it for. We do not use it for a purpose not listed here.',
      ],
      table: {
        columns: ['Personal data', 'Purpose'],
        rows: [
          [
            'Your name and email address',
            'To create your account, sign you in, and send you service messages such as verification and password resets',
          ],
          [
            'Your password',
            'To authenticate you. Stored only as a cryptographic hash, never in readable form',
          ],
          [
            'Your profile picture, language and application preferences, including AI style and personalisation settings',
            'To personalise the product for you',
          ],
          [
            'Transcripts of your meetings, and translations of them',
            'To show you what was said, live and afterwards',
          ],
          [
            'Questions detected in the conversation, and the AI answers generated for them',
            'To provide the assistance the product exists for',
          ],
          [
            'Summaries, action items, insights and Meeting Memory for a meeting',
            'To give you a usable, searchable record after the meeting ends',
          ],
          [
            'Documents you upload (including CVs and PDFs), the text extracted from them, and the numerical representations used to search them',
            'So the assistant can use your own material when answering',
          ],
          [
            'Your per-document “Exact Document Answers” choice for each meeting',
            'To decide how each document is used in that meeting (see “Your documents”, below)',
          ],
          [
            'Screenshots you attach to a question (sent for analysis; not stored by us — see “Screenshots”, below)',
            'To answer questions about what is on your screen',
          ],
          [
            'Your subscription status, meeting-pass purchases, and identifiers from our payment processor',
            'To manage your plan and take payment. We never see or store your card or bank details',
          ],
          [
            'The devices signed in to your account',
            'So you can see them and sign them out',
          ],
          [
            'Server logs of requests, error reports, and an audit log of administrative actions',
            'To keep the service secure, to fix faults, and to investigate abuse or incidents',
          ],
        ],
      },
      after: [
        'We rely on your consent, given when you create an account and accept these terms, and on the legitimate uses the DPDP Act permits — such as keeping the service secure and meeting our legal obligations.',
        'We do not sell personal data, and we do not run advertising or behavioural profiling. We do not use your meeting content to train our own AI models. The AI providers we use handle the data they receive under their own terms — see “Who we share data with”.',
      ],
    },
    {
      id: 'audio',
      title: 'Audio and recordings',
      body: [
        'Live meetings (web and desktop apps): audio is streamed to a speech-recognition provider, converted to text, and discarded. KrishvaQ does not write live meeting audio to disk or save it in our database. Only the resulting text transcript is kept.',
        'Auto Attend meetings are different. The meeting bot is software that we run on our own servers, and it records the meeting it joins. If the live transcript is missing or incomplete, we use that recording to produce the transcript afterwards.',
        'We have not set an automatic deletion schedule for Auto Attend recordings, so they stay on our systems until they are deleted. You can ask us to delete a recording by writing to us (see “Your rights”).',
        'So we do not say that audio is never recorded: live meetings in the apps are not recorded, but Auto Attend meetings are.',
      ],
    },
    {
      id: 'auto-attend',
      title: 'Auto Attend',
      body: [
        'If you use Auto Attend, a bot joins the Google Meet or Microsoft Teams meeting you choose, under the name set by your account (by default, your full name). It captures the meeting’s audio so KrishvaQ can transcribe it, answer questions and write a summary, and it may be recorded as described in “Audio and recordings”.',
        'What other participants say is captured too. Obtaining their agreement to being recorded or transcribed is your responsibility — see our Terms of Service.',
      ],
    },
    {
      id: 'screenshots',
      title: 'Screenshots and screen analysis',
      body: [
        'When you attach screenshots to a question, they are sent to an AI provider together with your question and the surrounding meeting context, so it can read the screen and answer. We keep the question and the AI’s answer in your meeting history. We do not save the screenshot images themselves.',
        'Screenshots can show other people’s information. Only capture what you are entitled to share.',
      ],
    },
    {
      id: 'documents',
      title: 'Your documents',
      body: [
        'Documents you upload are stored on our servers, linked to your account, and processed so they can be searched: the text is extracted and split into passages, and a numerical representation of each passage is created by an AI provider so that relevant passages can be found.',
        'Each document you attach to a meeting has its own “Exact Document Answers” switch. Documents are searched whichever way the switch is set; the switch controls how they answer. With it ON, a question that matches a question-and-answer entry in the document is answered with the document’s own text, unchanged. With it OFF, relevant passages are sent to the AI provider as reference material and the AI answers in its own words, still naming the document it used.',
        'In both cases, the passages that match a question are sent to an AI provider to produce the answer. Only your own documents are ever searched for your own meetings.',
        'Deleting a document removes the uploaded file. The text we extracted from it and the search data we made from it are marked as deleted and no longer used.',
      ],
    },
    {
      id: 'other-participants',
      title: 'Other people in your meetings',
      body: [
        'When you transcribe a meeting, what other participants say is captured too. We process that on your instruction, for you, and for no other purpose — it is not used to build profiles of them, it is not shared with anyone beyond the processors listed below, and it is deleted when you delete the meeting.',
        'Obtaining their agreement to being recorded is your responsibility, not ours. Our Terms of Service set that out, and you confirm it when you accept them.',
      ],
    },
    {
      id: 'sharing',
      title: 'Who we share data with',
      body: [
        'We use the following service providers to deliver KrishvaQ. Each processes data on our behalf to provide its part of the service, and handles that data under its own terms and security practices.',
      ],
      table: {
        columns: ['Processor', 'What it receives', 'Used for'],
        rows: [
          [
            'Sarvam AI',
            'Meeting audio, and text for translation',
            'Speech recognition and translation, where this provider is used',
          ],
          [
            'Google Cloud (Speech-to-Text)',
            'Meeting audio',
            'Speech recognition, for meetings where this provider is used',
          ],
          [
            'OpenAI',
            'Transcript text, passages from your documents, screenshots you attach, and meeting content',
            'Detecting questions; generating answers, summaries and search results; and creating the numerical representations used to search your documents',
          ],
          [
            'Google Identity',
            'Your name, email address and Google account identifier, only if you choose to sign in with Google',
            'Sign-in',
          ],
          [
            'Razorpay',
            'Your name, email address and payment details',
            'Taking payment for plans and meeting passes',
          ],
          [
            'Our email provider',
            'Your email address and the contents of service emails we send you',
            'Verification, password-reset and account emails',
          ],
          [
            'Amazon Web Services',
            'Everything described above, as stored data',
            'Hosting and database storage',
          ],
          [
            'Error monitoring (Sentry), if enabled',
            'Technical error reports, with personal data scrubbed',
            'Finding and fixing faults',
          ],
        ],
      },
      after: [
        'OpenAI’s published terms say that data sent to its API is not used to train its models unless the customer opts in, and that it may keep that data for up to 30 days to monitor for abuse. Other providers have their own retention practices, which we do not control.',
        'Some of these providers process data outside India, so your meeting content may leave the country. The DPDP Act permits transfers abroad except to countries the Central Government restricts.',
        'We may also disclose data where the law requires it, or to establish or defend legal claims.',
      ],
    },
    {
      id: 'retention',
      title: 'How long we keep it, and deleting it',
      body: [
        'We keep your meeting content for as long as your account is open, because searching your past meetings is a feature of the product. We have not set an automatic expiry for it.',
        'Deleting a meeting in the app permanently destroys its transcript, its translations, the AI answers generated in it, and the saved answers that quoted it.',
        'Deleting your account (Settings → Security → Delete account) cancels any paid subscription, signs you out everywhere, and permanently destroys every meeting on the account. Your account record, uploaded documents and other account data are marked as deleted and removed from the product, but we do not erase them from our database automatically. You can ask us to erase them completely by writing to us (see “Your rights”).',
        'We keep backups of our database for disaster recovery, so deleted data can remain in a backup until newer backups replace it. We keep billing and payment records for as long as tax and accounting law requires, even after an account is deleted. Auto Attend recordings are described in “Audio and recordings”.',
      ],
    },
    {
      id: 'payments',
      title: 'Payments and billing',
      body: [
        'Payments for subscriptions and meeting passes are taken by Razorpay. We never see or store your card or bank details. We keep the payment identifiers, amounts, status and dates Razorpay returns to us, so we can give you access, show your payment history, issue receipts, and handle refunds. Subscription payments are not refundable except as our Terms of Service describe (see “Payments, Cancellation & No-Refund Policy”).',
      ],
    },
    {
      id: 'your-rights',
      title: 'Your rights',
      body: [
        'As a Data Principal under the DPDP Act you have the following rights.',
      ],
      list: {
        items: [
          'Access — obtain a summary of the personal data we hold about you and who we have shared it with.',
          'Correction and completion — have inaccurate or incomplete data corrected. You can edit most of it yourself in Settings.',
          'Erasure — have your data deleted. You can delete meetings and your account yourself at any time (see above), or write to us to ask for complete erasure.',
          'Withdraw consent — as easily as you gave it. Withdrawing it means we stop processing and delete what we no longer have a lawful reason to keep.',
          'Nominate — nominate another individual to exercise these rights on your behalf in the event of your death or incapacity. Write to us to record a nomination.',
          'Grievance redressal — raise a complaint with us and have it answered.',
        ],
      },
      after: [
        `To exercise any of these, to request deletion of your data, or to raise a grievance, write to our Grievance Officer at ${COMPANY.privacyEmail}. We aim to respond within 30 days, and in any case within the time the law allows.`,
        'If you are not satisfied with our response, you may complain to the Data Protection Board of India, as the law provides.',
      ],
    },
    {
      id: 'your-duties',
      title: 'Your duties',
      body: [
        'The DPDP Act also places duties on you: do not impersonate someone else when giving us personal data, do not suppress material information, do not register a false or frivolous grievance, and give us only information that is authentic when exercising a right to correction.',
      ],
    },
    {
      id: 'security',
      title: 'How we protect it',
      body: [
        'Data is encrypted in transit. Passwords are stored only as cryptographic hashes. Access to production systems is restricted, and administrative actions are recorded in an audit log. On the desktop app, your sign-in tokens are stored in your operating system’s keychain rather than in a file.',
        'No system is perfectly secure. If a breach affects your personal data we will notify you and the Data Protection Board of India, as the DPDP Act requires.',
      ],
    },
    {
      id: 'children',
      title: 'Children',
      body: [
        'KrishvaQ is not intended for anyone under 18, and we do not knowingly collect data from children. If you believe a child has given us personal data, contact us and we will delete it.',
        'We do not track or behaviourally monitor children, and we do not direct advertising at them. We do not run behavioural advertising at all.',
      ],
    },
    {
      id: 'changes',
      title: 'Changes to this policy',
      body: [
        'If we make a material change we will tell you in the application and ask you to review the updated policy before continuing to use the service. The version and effective date of the policy you are reading are shown at the top of this page.',
      ],
    },
    {
      id: 'contact',
      title: 'Contact',
      body: [
        `Privacy questions and requests: ${COMPANY.privacyEmail}. ${COMPANY.legalName}, ${COMPANY.address}.`,
      ],
    },
  ],
};

export const TERMS_OF_SERVICE = {
  id: 'terms',
  path: TERMS_PATH,
  title: 'Terms of Service',
  intro:
    'These terms govern your use of KrishvaQ, a product of ' +
    `${COMPANY.legalName}. By creating an account or using the service you ` +
    'agree to them. Please read the sections on recording and consent and ' +
    'on what the AI can and cannot do carefully — the first describes a ' +
    'legal responsibility that rests with you, not with us.',
  sections: [
    {
      id: 'the-service',
      title: 'The service',
      body: [
        'KrishvaQ transcribes and translates meetings in real time, and uses artificial intelligence to suggest answers, summaries and action items. It includes live answers during meetings and interviews, screenshot analysis, answers based on documents you upload, meeting summaries and memory, and an Auto Attend agent that can join meetings for you. It is available as a web application and as a desktop application for Windows and macOS.',
        'We may change, add or remove features. If we remove something significant we will give you reasonable notice.',
      ],
    },
    {
      id: 'your-account',
      title: 'Eligibility and your account',
      body: [
        'You must be at least 18 and provide accurate information. You are responsible for everything done through your account and for keeping your password secure. Tell us promptly if you believe someone else has access to it.',
        'You may not share one account between multiple people.',
      ],
    },
    {
      id: 'recording-consent',
      title: 'Recording and consent — your responsibility',
      emphasis: true,
      body: [
        'Laws on recording conversations differ by country and, in the United States, by state. In many places every participant must consent before a conversation may be recorded or transcribed, and recording without that consent can be a criminal offence.',
        'You are solely responsible for obtaining whatever consent the law requires from every participant in any meeting you record or transcribe using KrishvaQ, and for complying with the rules of the meeting platform you are using, and of any employer, school or interviewer whose meeting it is.',
        'You confirm, each time you use KrishvaQ to record or transcribe a meeting, that you have obtained that consent.',
        'KrishvaQ does not notify other participants that a meeting is being transcribed (except that an Auto Attend bot appears in the participant list), and the desktop overlay is deliberately hidden from screen sharing so that your private notes are not exposed when you share your screen. That design does not reduce your obligation to tell participants and obtain their consent — if anything it increases it, because they will not learn of it from the software.',
        'We are not responsible for your failure to obtain consent, and you agree to indemnify us against claims arising from it.',
      ],
    },
    {
      id: 'auto-attend',
      title: 'Auto Attend',
      body: [
        'Auto Attend sends a bot into a Google Meet or Microsoft Teams meeting that you choose. You are responsible for: having the right to invite a bot into that meeting; telling the other participants that the meeting is being captured and may be recorded; admitting the bot only to meetings where that is allowed; and ending or removing it when it should not be there.',
        'Some organisations and meeting hosts prohibit bots, note-takers or recording. It is your responsibility to check, and to comply.',
      ],
    },
    {
      id: 'overlay',
      title: 'Private overlay and screen sharing',
      body: [
        'The desktop overlay is designed to stay out of screen sharing on supported setups. We do not guarantee that it is invisible on every operating system, screen-sharing tool, capture method or version, and it can fail. Do not rely on it to keep anything hidden.',
        'You are responsible for how you use KrishvaQ in an interview, exam, assessment or any other setting that has rules about outside help. Using an AI assistant where it is not allowed can have serious consequences for you, and we are not responsible for them.',
      ],
    },
    {
      id: 'ai-limitations',
      title: 'What the AI can and cannot do',
      emphasis: true,
      body: [
        'KrishvaQ uses automated speech recognition and large language models. Both make mistakes. Transcripts may misrepresent what was said, particularly with accents, background noise, technical vocabulary or overlapping speakers. Translations may be inaccurate. AI-generated answers, summaries and action items may be incomplete, misleading, or confidently wrong, including code and technical facts.',
        'KrishvaQ’s output is a draft aid, not a record of fact. Do not rely on it as a verbatim transcript, as legal, medical, financial or professional advice, or as the sole basis for any decision that matters. Always check AI-generated answers, and verify anything important against the underlying conversation or a reliable source.',
        'We do not warrant that output will be accurate, complete or fit for any particular purpose.',
      ],
    },
    {
      id: 'acceptable-use',
      title: 'Acceptable and prohibited use',
      body: ['You may not use KrishvaQ to:'],
      list: {
        items: [
          'Record or transcribe anyone without the consent the law requires.',
          'Break any law, or infringe anyone’s privacy or intellectual property rights.',
          'Cheat, or breach the rules of an exam, assessment or interview that forbids outside assistance.',
          'Upload malware, or attempt to breach, overload or reverse-engineer the service.',
          'Resell or redistribute the service without our written agreement.',
          'Circumvent usage limits or access another user’s data.',
        ],
      },
      after: [
        'We may suspend or terminate an account that breaches these terms. Where the breach is not serious we will normally contact you first.',
      ],
    },
    {
      id: 'your-content',
      title: 'Your documents and content',
      body: [
        'Your meetings, transcripts and documents remain yours. You confirm that you have the right to upload any document you add, and that doing so does not breach anyone else’s rights or confidentiality.',
        'You grant us only the licence we need to operate the service for you — to store your content, process it through the providers listed in our Privacy Policy, and display it back to you. The “Exact Document Answers” switch on each document controls how it is used in a meeting, as described in the Privacy Policy.',
        'We do not sell your content, and we do not train our own AI models on it.',
      ],
    },
    {
      id: 'payments-cancellation',
      title: 'Payments, Cancellation & No-Refund Policy',
      emphasis: true,
      body: [
        'Prices and what each plan includes are shown before you pay. Payments are taken by Razorpay. Subscription plans are billed in advance for the monthly or yearly cycle you choose, and renew automatically at the end of each cycle until you cancel. We will give you notice before a price change applies to your renewal.',
        'A Meeting Pass is a one-time payment for a single meeting. It does not renew.',
        'Please read the rules below before you pay. By completing a payment you accept them.',
      ],
      list: {
        items: [
          'Subscription purchases are non-refundable. Once a subscription payment is completed, it is not refunded — including for any part of the billing period that you have not used.',
          'You can cancel a subscription at any time in the app (Billing). Cancelling stops future renewals. It does not refund the current billing period, and you keep your plan until that period ends.',
          'To avoid being charged for the next period, cancel before your renewal date.',
          'Downgrading, no longer using KrishvaQ, or deleting your account does not entitle you to a refund of a payment you have already made.',
        ],
      },
      after: [
        'Refunds and remedies required by law. Nothing in this section limits any refund, reversal or other remedy that applicable law gives you. This includes the cases where you were charged more than once or a different amount from the price shown, where a payment was taken but your plan could not be activated, where a payment was made without your authority, and where we failed to provide the service you paid for. In those cases we will correct the problem, which may include a refund.',
        `To report a payment problem, write to ${COMPANY.supportEmail} from the email address on your KrishvaQ account, with the date and amount of the payment and the payment reference from your receipt. We review each report on its own facts.`,
      ],
    },
    {
      id: 'availability',
      title: 'Service availability',
      body: [
        'We aim to keep KrishvaQ available but do not guarantee uninterrupted service. It depends on third-party providers, and maintenance and outages happen. The service is provided "as is" and "as available".',
      ],
    },
    {
      id: 'liability',
      title: 'Liability',
      body: [
        'Nothing in these terms limits liability that cannot lawfully be limited, including for death or personal injury caused by negligence, or for fraud.',
        'Subject to that, we are not liable for indirect or consequential loss, for lost profits, business or data, or for any loss arising from your reliance on AI-generated output or your failure to obtain recording consent. Our total liability in any 12-month period is limited to the amount you paid us in that period.',
      ],
    },
    {
      id: 'termination',
      title: 'Suspension and termination',
      body: [
        'You may stop using KrishvaQ and delete your account at any time in Settings → Security. Deleting your account permanently destroys your meeting content and cancels any paid subscription, as described in the Privacy Policy.',
        'We may suspend or terminate your access if you breach these terms, or if we discontinue the service — in which case we will give you reasonable notice and an opportunity to retrieve your content.',
      ],
    },
    {
      id: 'governing-law',
      title: 'Governing law',
      body: [
        `These terms are governed by the laws of India, and the courts of ${COMPANY.jurisdiction} have exclusive jurisdiction.`,
      ],
    },
    {
      id: 'contact',
      title: 'Contact',
      body: [
        `Questions about these terms: ${COMPANY.supportEmail}. ${COMPANY.legalName}, ${COMPANY.address}.`,
      ],
    },
  ],
};

/**
 * The short form of the no-refund rule, shown on the plan-selection screens
 * (web and desktop) BEFORE a customer pays. It must agree with the
 * 'payments-cancellation' section of the Terms, which it links to.
 */
export const PAYMENT_NOTICE = {
  text:
    'Subscription purchases are non-refundable, including any unused part of ' +
    'the billing period. Cancelling stops future renewals but does not ' +
    'refund the current billing period. Refunds or remedies that the law ' +
    'requires are not affected.',
  linkLabel: 'Payments, Cancellation & No-Refund Policy',
  /** Section of the Terms of Service page. */
  path: `${TERMS_PATH}#payments-cancellation`,
};

export const LEGAL_DOCUMENTS = [PRIVACY_POLICY, TERMS_OF_SERVICE];
